import { t } from "i18next";
import LabeledField from "providers/labeledField";
import GenericCard from "widgets/cards/generic";
import { Alert } from "api/types/alerts";
import { formatDate } from "util/datelib";
import Badge from "widgets/shared/badge";
import { getAlertStateColor } from "util/status";
import CircleButton from "widgets/controls/buttons/circle-button";
import { MdOutlineNotifications } from "react-icons/md";
import { useState } from "react";
import AlertNotificationsDialog from "widgets/dialogs/alertNotifications";

interface TimelineEvent {
  username: string;
  notifications: any;
  data: any;
  action: string;
  comment: string;
  created: string;
  user: string;
}

interface GeneralProps {
  alert?: Alert;
  onUpdate?: () => void;
  selectedEvent?: TimelineEvent;
}

const Alerts: React.FC<GeneralProps> = ({ alert, onUpdate, selectedEvent }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  // Calculate event number based on chronological order (oldest = 1, newest = last)
  const getEventNumber = () => {
    if (!alert?.timeline || !selectedEvent) return null;

    // Sort events by date (oldest first) to get chronological order
    const sortedEvents = [...alert.timeline].sort(
      (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
    );

    // Find the index of the selected event in the chronological order
    const eventIndex = sortedEvents.findIndex(
      (event) =>
        event.created === selectedEvent.created &&
        event.username === selectedEvent.username &&
        event.action === selectedEvent.action
    );

    return eventIndex !== -1 ? eventIndex + 1 : null;
  };

  const eventNumber = getEventNumber();

  return (
    <>
      {showNotifications && (
        <AlertNotificationsDialog
          notifications={selectedEvent?.notifications || []}
          isOpen={showNotifications}
          onClose={() => {
            setShowNotifications(false);
          }}
        />
      )}
      <GenericCard
        title={t("alert.timeline.event.details")}
        bgColor="900"
        extraTitle="heading-md h-fit"
        topRight={
          <div className="-mt-2 flex items-center">
            {selectedEvent && (
              <Badge
                sizes="sm"
                variant="fill"
                label={selectedEvent.action}
                color={getAlertStateColor(selectedEvent.action)}
              />
            )}
            <CircleButton
              icon={MdOutlineNotifications}
              size="md"
              onClick={() => {
                setShowNotifications(true);
              }}
              variant="ghost"
              tooltip={t("tooltip.notifications.show")}
              tooltipPlacement="bottom"
            />
          </div>
        }
      >
        {selectedEvent ? (
          // Show selected timeline event details
          <div className="space-y-4">
            {eventNumber && (
              <div className="text-txt-sm text-primary">
                <span className="font-medium">
                  {t("alert.timeline.event.number")}:
                </span>{" "}
                {eventNumber}
              </div>
            )}

            <LabeledField
              className="text-txt-md"
              textColor="secondary"
              label={t("alert.timeline.event.user")}
              id={"alert-timeline-event-user"}
            >
              <div className="text-txt-lg text-primary">
                {selectedEvent.user
                  ? selectedEvent.user
                  : selectedEvent.username || t("alert.timeline.event.user")}
              </div>
            </LabeledField>

            <LabeledField
              className="text-txt-md"
              textColor="secondary"
              label={t("alert.timeline.event.timestamp")}
              id={"alert-timeline-event-timestamp"}
            >
              <div className="text-txt-lg text-primary">
                {formatDate(selectedEvent.created, "lastcomms") ||
                  t("alert.timeline.event.fallback.created")}
              </div>
            </LabeledField>

            <LabeledField
              className="text-txt-md"
              textColor="secondary"
              label={t("alert.timeline.event.action")}
              id={"alert-timeline-event-action"}
            >
              <div className="text-txt-lg text-primary">
                {selectedEvent.action ||
                  t("alert.timeline.event.fallback.created")}
              </div>
            </LabeledField>

            <LabeledField
              className="text-txt-md"
              textColor="secondary"
              label={t("alert.timeline.event.comment")}
              id={"alert-timeline-event-comment"}
            >
              {selectedEvent.comment && (
                <div className="mt-1 p-2 text-txt-lg text-primary">
                  {selectedEvent.comment}
                </div>
              )}
            </LabeledField>

            <LabeledField
              className="text-txt-md"
              textColor="secondary"
              label={t("alert.timeline.event.data")}
              id={"alert-timeline-event-data"}
            >
              <div className="flex-1">
                {selectedEvent.data &&
                typeof selectedEvent.data === "object" ? (
                  Object.keys(selectedEvent.data).length > 0 && (
                    <div className="mt-1 p-2 text-txt-lg text-primary">
                      {t("alert.timeline.event.data", {
                        type: selectedEvent.data?.type,
                      })}
                      : {selectedEvent.data?.value} {selectedEvent.data?.unit}
                    </div>
                  )
                ) : (
                  <div className="mt-1 p-2 text-txt-lg text-primary">{"-"}</div>
                )}
              </div>
              {/* <div className="text-txt-lg text-primary">{JSON.stringify(selectedEvent.data) || t("alert.timeline.event.fallback.created")}</div> */}
            </LabeledField>
          </div>
        ) : (
          // Show default alert information
          <>
            <div className="text-txt-md text-primary">
              {t("alert.timeline.event.fallback.no.event")}
            </div>
          </>
        )}
      </GenericCard>
    </>
  );
};

export default Alerts;
